import React, {useState, useEffect, useCallback} from 'react';
import { BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import LayoutWrapper from '../components/LayoutWrapper';
import VenueAddressForm from '../components/VenueAddressForm';
import Helper from '../helpers';
import config from '../config';

const baseUrl = config.apiBaseUrl;

const VenueSettings = () => {
    const [venueKey, setVenueKey]       = useState('');
    const [venue, setVenue]             = useState(null);
    const [initialAddress, setInitialAddress] = useState({});
    const [loading, setLoading]         = useState(true);
    const [error, setError]             = useState('');

    const fetchVenueData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const token = await Helper.getCookie('wh-jwt');

            // Fetch both endpoints in parallel
            const [venueRes, listingRes] = await Promise.all([
                fetch(`${baseUrl}/api/venue/public/${venueKey}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                fetch(`${baseUrl}/api/ptdirectory/my-venue-listing`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);

            // Venue identity (name, key)
            if (venueRes.ok) {
                const venueData = await venueRes.json();
                setVenue(venueData);
            }

            // Address — prefer the ptdirectory endpoint which resolves
            // Line1/Line2/City/Postcode from Venue.Address via the repository
            if (listingRes.ok) {
                const listingData = await listingRes.json();
                const l = listingData?.listing;
                if (l) {
                    setInitialAddress({
                        line1:    l.line1    || '',
                        line2:    l.line2    || '',
                        line3:    '',          // Line3 (district) not in directory response
                        city:     l.city     || '',
                        county:   '',          // County not in directory response
                        postCode: l.postcode || '',
                    });
                }
            }
        } catch (e) {
            setError('Could not load venue details. Please try again.');
            console.error('VenueSettings fetch error:', e);
        } finally {
            setLoading(false);
        }
    }, [venueKey]);
    
    useEffect(() => {
        const init = async () => {
            const key = await Helper.getUserVenueId();
            if (key) setVenueKey(key);
        };
        init();
    }, []);

    useEffect(() => {
        if (!venueKey) return;
        fetchVenueData();
    }, [venueKey,fetchVenueData]);

    return (
        <LayoutWrapper
            title="Venue Settings"
            subtitle="Manage your venue's core details"
        >
            {loading && (
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-400" />
                </div>
            )}

            {error && (
                <div className="bg-red-900/30 border border-red-700 text-red-300 rounded-lg px-4 py-3 mb-6 text-sm">
                    {error}
                </div>
            )}

            {!loading && venueKey && (
                <div className="space-y-8 max-w-2xl">
                    {/* Venue identity banner */}
                    <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 flex items-center gap-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-emerald-600/20 rounded-full flex items-center justify-center">
                            <BuildingStorefrontIcon className="h-6 w-6 text-emerald-400" />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Venue Key</p>
                            <p className="text-white font-semibold font-mono">{venueKey}</p>
                            {venue?.venueName && (
                                <p className="text-gray-400 text-sm mt-0.5">{venue.venueName}</p>
                            )}
                        </div>
                    </div>

                    {/* Address card — pre-populated from ptdirectory/my-venue-listing */}
                    <VenueAddressForm
                        key={`${initialAddress.line1}-${initialAddress.postCode}`}
                        venueKey={venueKey}
                        initial={initialAddress}
                        onSaved={() => fetchVenueData()}
                    />
                </div>
            )}
        </LayoutWrapper>
    );
};

export default VenueSettings;


